import { Tabs, Skeleton } from '@round/creatorbase-ui';
import { creatorbase } from '@round/api';
import { ComponentProps } from 'react';

import { platformLabels, platformSolidIcons, platformIcons, platforms } from 'Modules/Plans/helpers';

import styles from './PlatformSelect.module.css';

const skeletonPlatformSlots = platforms.filter((platform) => platform !== 'youtube');

export type PlatformSelectProps = {
    availablePlatforms: creatorbase.Platform[];
    isLoading?: boolean;
} & Pick<ComponentProps<typeof Tabs>, 'value' | 'defaultValue' | 'onValueChange'>;

const PlatformSelect = ({ availablePlatforms, isLoading, value, defaultValue, onValueChange }: PlatformSelectProps) => {
    if (isLoading) {
        return (
            <div className={styles.list} aria-busy="true" aria-label="Loading platforms">
                {skeletonPlatformSlots.map((platform) => (
                    <Skeleton key={`platform-skeleton-${platform}`} className={styles.triggerSkeleton} />
                ))}
            </div>
        );
    }

    if (!availablePlatforms.length) {
        return null;
    }

    const resolvedDefault = value === undefined ? defaultValue ?? platforms[0] : undefined;
    // filtering platforms keeps the order of the platforms
    const platformsToShow = platforms.filter((platform) => availablePlatforms.includes(platform));
    return (
        <Tabs value={value} defaultValue={resolvedDefault} onValueChange={onValueChange}>
            <Tabs.List className={styles.list}>
                {platformsToShow.map((platform) => {
                    const Icon = platformIcons[platform];
                    const SolidIcon = platformSolidIcons[platform];
                    const isActive = value !== undefined ? value === platform : defaultValue === platform;
                    const PlatformIcon = isActive ? SolidIcon : Icon;
                    return (
                        <Tabs.Trigger key={platform} value={platform} className={styles.trigger}>
                            <PlatformIcon className={styles.icon} aria-hidden />
                            {platformLabels[platform]}
                        </Tabs.Trigger>
                    );
                })}
            </Tabs.List>
        </Tabs>
    );
};

export default PlatformSelect;
